import dayjs from 'dayjs';
import { useStoreState } from 'easy-peasy';
import React, { useEffect, useState, useContext, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import styled from 'styled-components';

import { BoldSearch } from '@ge/components/bold-search';
import { Button } from '@ge/components/button';
import { Filter } from '@ge/components/filter';
import { Icon, Icons } from '@ge/components/icon';
import { Dialog, WarningDialog } from '@ge/components/modal';
import { useNotification } from '@ge/components/notification';
import { ScrollingContainer } from '@ge/components/scrolling-container';
import useServiceGroupSearch from '@ge/feat-analyze/data-hooks/use-service-group-search';
import NotificationMessage from '@ge/feat-monitor/components/notification-message';
import {
  Capability,
  DateTimeFormats,
  NotificationType,
  TaskSourceField,
} from '@ge/models/constants';
import { AuthRender } from '@ge/shared/components/auth-render';
import Breadcrumbs from '@ge/shared/components/breadcrumbs';
import { NewTaskDialog } from '@ge/shared/components/tasks/new-task-dialog';
import { elevations, globalColors } from '@ge/tokens';

import { Calendar } from '../components/planning/calendar';
import { NeedsAttention } from '../components/planning/needs-attention';
import { PlanningTabs } from '../components/planning/planning-tabs';
import {
  PlanningProvider,
  PlanningContext,
  FilterValues,
  DefaultFilters,
} from '../context/planning-provider';
import { useCrews } from '../data-hooks/use-crews';
import { useErpNotifications } from '../data-hooks/use-erp-notifications';
import { useTaskBacklog } from '../data-hooks/use-task-backlog';
import { useTaskSchedule } from '../data-hooks/use-task-schedule';
//import { useTasksTable } from '../data-hooks/use-tasks-table';
// import { useWorkers } from '../data-hooks/use-workers';

const NOTIFICATION_DELAY = 1000;
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;

  .overview-page-title {
    padding: 5px 18px 5px 18px;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex: 1;
  padding: 0 10px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  padding-right: 20px;
  button {
    align-self: center;
  }
`;

const StyledNewTaskButton = styled(Button)`
  position: relative;
  top: 6px;
`;

const CloseButton = styled.button`
  position: absolute;
  right: 6px;
  display: inline-block;
  vertical-align: middle;
  padding: 10px;
`;
const DataContent = styled.div`
  color: ${({ theme }) => theme.caseLinkDialog.warningDialogColor};
  text-align: center;
  font-size: 13px;
  line-height: 16px;
  margin: 10px 0px;
  padding-top: 30px;
  width: 392px;
  padding-bottom: 10px;
`;
const CloseIcon = styled(Icon).attrs((props) => ({
  color: props.theme.sidebar.settingsIconColor,
}))`
  display: block;
`;

const StyledWarningDialog = styled(WarningDialog)`
  text-align: center;
  width: 435px;
  z-index: 100000px;
  text-align: -webkit-center;
`;

const SelectorPanel = styled.div`
  position: relative;
  flex-direction: column;
  flex: 1;
  margin-right: -30px;
  width: 194px;
  color: ${globalColors.grey1};
  z-index: ${elevations.P2};
  transition: false;
  box-sizing: border-box;
  text-align: -webkit-left;
  &.active {
    display: flex;
  }
`;

const SearchHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledFilter = styled(Filter)`
  text-align: center;
  position: relative;
  input {
    color: ${globalColors.white};
    box-sizing: border-box;
    height: 28px;
    width: 100%;
    border: 1px solid ${({ theme }) => theme.input.borderNavbarInput};
    border-radius: 2px;
    background-color: ${({ theme }) => theme.themeSelector.tabsBackgroundColor};
    caret-color: ${globalColors.teal3};
    ::placeholder {
      font-size: 14px;
      font-style: italic;
    }
    :focus {
      outline: none;
    }
  }
  .search-icon {
    position: absolute;
    top: 7px;
    right: 5px;
  }
`;

const ResultsContainer = styled.div`
  display: none;
  top: 10px;
  width: 90%;
  overflow: hidden;
  position: absolute;
  margin-top: 18px
  background: ${({ theme }) => theme.themeSelector.tabsBackgroundColor};
  &.show-results,
  &:hover,
  &:focus,
  &:focus-within {
    display: block;
  }
`;

const Results = styled.div`
  display: flex;
  position: relative;
  flex: 1;
  max-height: 200px;
`;

const ServiceGroupIcon = styled(Icon).attrs(() => ({
  size: 12,
  icon: Icons.SERVICE_GROUP,
  color: globalColors.slate4,
}))`
  margin-right: 12px;
`;

const ServiceGroupName = styled.div`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const StyledLi = styled.li`
  border-bottom: 1px solid ${globalColors.slate12};
  max-height: 30px;
  color: ${globalColors.grey7};
  padding: 6px 8px;
  font-size: 10px;
  display: flex;
  align-items: center;
  cursor: pointer;
  width: 100%;
  box-sizing: border-box;
  &:hover,
  &:focus {
    background-color: ${globalColors.teal4};
    border-bottom: 1px solid ${globalColors.teal7};
  }
  &:first-child {
    border-top: 1px solid ${globalColors.slate12};
  }
`;

const StyledUl = styled.ul`
  list-style: none;
  margin-top: 0;
  padding-left: 0;
  margin-bottom: 0;
`;

const ConfirmButton = styled(Button)`
  width: 107px;
  height: 25px;
  background: linear-gradient(180deg, #006e71 1.04%, #005e60 97.9%);
  color: #FFFFF;
  font-size: 13px;
  line-height: 15.6px;
  text-align: center;
  margin-top: 15px;
  border-radius: 4px;
  border: 1px solid #005e60;
`;

const ButtonSection = styled.div`
  display: flex;
  justify-content: end;
  margin-top: 10px;
`;

const CancelButton = styled(Button)`
  border-radius: 4px;
  border: 1px solid ${(props) => props.theme.deleteCaseTemplate.cancelBorderColor};
`;

const ConfirmDialogButton = styled(Button)`
  border-radius: 4px;
  margin: 0 20px 0 10px;
`;

const ManageOverview = () => {
  const { ready, t } = useTranslation(['manage.planning', 'manage.erp-statuses'], {
    useSuspense: false,
  });
  const { notify } = useNotification();
  const {
    planningState: { range },
    serviceGroupIds,
  } = useContext(PlanningContext);
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [closeDialog, setCloseDialog] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const parentRef = useRef();

  const { push } = useHistory();
  const { serviceGroupId: selectedServiceGroupId } = useParams();

  const routeToServiceGroup = (id) => {
    setFilterText('');
    setShowResults(false);
    push(`/execute/overview/${id}`);
  };
  const onSelectedGroupSelect = (id, name) => {
    setFilterText(name);
    setShowResults(false);
  };

  const { serviceGroups } = useServiceGroupSearch(filterText);

  /*
    onMouseDown vs onClick fires before parent onBlur event
    Otherwise onBlur fires first and the onClick event
    is removed with the hidden element (the nav site list)
  */
  const serviceGroupItemFn = ({ id, name }) => (
    <StyledLi
      key={id}
      id={'search-service-group-overview'}
      onClick={(e) => {
        onSelectedGroupSelect(id, name);
        e.preventDefault();
        e.stopPropagation();
      }}
      onKeyUp={(e) => {
        if (e.key === 'Enter') {
          onSelectedGroupSelect(id, name);
        }
        if (e.key === 'Escape') {
          setShowResults(false);
        }
      }}
      tabIndex="0"
    >
      <ServiceGroupIcon className="wind-icon" />
      <ServiceGroupName>
        <BoldSearch text={name} textBold={filterText} />
      </ServiceGroupName>
    </StyledLi>
  );

  useEffect(() => {
    const isNumeric = (str) => Number.isFinite(+str);
    if (!isNumeric(selectedServiceGroupId) && !isNumeric(serviceGroupIds[0])) {
      if (
        serviceGroups &&
        selectedServiceGroupId &&
        !serviceGroups?.find((group) => group.id === selectedServiceGroupId)
      ) {
        push(`/execute/overview`);
      }
      if (serviceGroupIds.length === 1 && serviceGroupIds[0] !== selectedServiceGroupId) {
        push(`/execute/overview/${serviceGroupIds}`);
      }
    }
  }, [push, selectedServiceGroupId, serviceGroupIds, serviceGroups]);

  const isAssetsLoading = useStoreState(({ assets }) => assets.isLoading);

  const getAutomatedTaskGroups = useStoreState((state) => state.siteManager.getAutomatedTaskGroups);

  const scheduleData = getAutomatedTaskGroups(range);
  const { data: backlogData, isFetching: isFetching } = useTaskBacklog(selectedServiceGroupId);
  //WIP
  // const { data: workerData, isFetching: isFetchingWorkers } = useWorkers();
  const { data: crewData, isFetching: isFetchingCrewsWorkers } = useCrews(
    undefined,
    undefined,
    selectedServiceGroupId,
  );
  const newFieldTask = useCallback(() => {
    setShowNewTaskModal(true);
  }, [setShowNewTaskModal]);

  const closeModal = useCallback(() => {
    setShowNewTaskModal(false);
  }, [setShowNewTaskModal]);

  const saveTask = useCallback(() => {
    setShowNewTaskModal(false);
  }, []);

  const handleClose = () => {
    setShowConfirmationModal(true);
  };

  const onCancelModal = () => {
    setShowConfirmationModal(false);
  };

  const onConfirmModal = () => {
    setCloseDialog(false);
    setShowConfirmationModal(false);
  };

  const getConfirmationFooter = () => {
    return (
      <ButtonSection>
        <CancelButton onClick={onCancelModal}>{'Cancel'}</CancelButton>
        <ConfirmDialogButton primary onClick={onConfirmModal}>
          {'Confirm'}
        </ConfirmDialogButton>
      </ButtonSection>
    );
  };
  const { data: allTasksDisplay } = useTaskSchedule({
    start: range.start,
    end: range.end,
    groupByL1: FilterValues.SITE_ASSET,
    groupByL2: null,
    groupByFilters: DefaultFilters,
    selectedServiceGroupId,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  let allTasks = [];
  allTasksDisplay?.schedule.map((dateArray) => {
    dateArray?.data?.map((dateTasks) => {
      if (dateTasks?.tasks?.length) {
        dateTasks?.tasks.map((tasks) => {
          allTasks.push(tasks);
        });
      }
    });
  });

  // UPDATE: Call erpNotifications api if only backlogdata or schedular data available
  let loadErpNotify = false;
  if (backlogData?.length || allTasks?.length) {
    loadErpNotify = true;
  }
  const { data: erpNotifications, isFetching: isNotificationFetching } =
    useErpNotifications(loadErpNotify);

  const triggerNotification = useCallback(
    (notifications) => {
      if (!allTasks) {
        return;
      }
      notifications.forEach((notification, index) => {
        let notificationData = [];
        // Checking data available in scheduled data
        notificationData = allTasks.filter((el) => {
          return el?.id === notification?.taskId;
        });
        // Checking data available in backlog data
        if (notificationData?.length === 0) {
          notificationData = backlogData.filter((el) => {
            return el?.id === notification?.taskId;
          });
        }

        if (notificationData?.length) {
          const asset = {
            id: notificationData[0]?.asset?.id,
            name: notificationData[0]?.asset?.name,
          };
          const site = {
            id: notificationData[0]?.site?.id,
            name: notificationData[0]?.site?.name,
          };
          const taskName = {
            id: notificationData[0]?.id,
            name: notificationData[0]?.title,
          };

          // TODO: add method for the `onBreadCrumbClick` callback
          const status = (
            <Breadcrumbs
              onBreadCrumbClick={() => {
                console.log('breadcrumb clicked!');
              }}
              site={site}
              asset={asset}
              title={taskName}
              showChevron={true}
            />
          );

          // TODO: pass notification's message based on messageCode
          //t('case_created_success', 'Case Created Successfully').toUpperCase()(
          const message = (
            <NotificationMessage
              message={t(
                `manage.erp-statuses:status.${notification?.messageCode}`,
                notification?.messageCode,
              )}
            />
          );

          // Notification's timestamp
          const timestamp = dayjs(notification?.timestamp).format(
            DateTimeFormats.SR_NOTIFICATION_DATE_TIME,
          );

          // a failure will make the notification last longer on screen
          const type = notification.isFailure ? NotificationType.FAILURE : NotificationType.INFO;
          if (notificationData && notification) {
            setTimeout(() => {
              notify({ status, message, timestamp, type });
            }, NOTIFICATION_DELAY * index);
            delete notifications[index];
          }
        }
      });
    },
    [allTasks, backlogData, notify, t],
  );

  useEffect(() => {
    if (erpNotifications?.length && !isNotificationFetching) {
      triggerNotification(erpNotifications);
    }
  }, [erpNotifications, isNotificationFetching, triggerNotification]);

  if (!ready || !backlogData || !scheduleData) {
    return null;
  }

  return (
    <>
      <PageContainer>
        <Header>
          {serviceGroupIds.length > 1 && !selectedServiceGroupId && (
            <StyledWarningDialog isOpen={closeDialog} contentWidth padContent={true}>
              <CloseButton onClick={handleClose}>
                <CloseIcon icon={Icons.CLOSE} size={10} />
              </CloseButton>
              <DataContent>
                {t(
                  'service_group_message',
                  'You have multiple service groups selected. Please select one service group to proceed.',
                )}
              </DataContent>
              <SelectorPanel>
                <SearchHeader>
                  <StyledFilter
                    text={filterText}
                    onChange={(event) => setFilterText(event.target.value)}
                    showResults={() => {
                      setShowResults(true);
                    }}
                    onInputBlur={(event) => {
                      if (event.relatedTarget?.id !== 'search-service-group-overview') {
                        setShowResults(false);
                      }
                    }}
                  />
                </SearchHeader>

                {showResults && (
                  <ResultsContainer className="show-results">
                    <Results>
                      {serviceGroups?.length > 0 && (
                        <ScrollingContainer ref={parentRef} relative>
                          <StyledUl noPadding>
                            {serviceGroups.map(({ id, name }) => serviceGroupItemFn({ id, name }))}
                          </StyledUl>
                        </ScrollingContainer>
                      )}
                    </Results>
                  </ResultsContainer>
                )}
              </SelectorPanel>
              <ConfirmButton
                primary
                disabled={filterText !== serviceGroups[0]?.name}
                onClick={() => routeToServiceGroup(serviceGroups[0].id)}
              >
                {t('confirm', 'Confirm')}
              </ConfirmButton>
            </StyledWarningDialog>
          )}
          <h1 className="overview-page-title">{t('planning', 'planning')}</h1>
          {!isAssetsLoading && (
            <AuthRender
              capability={Capability.FIELD_TASKS}
              create
              description="New Task button"
              siteLevel={false}
            >
              <StyledNewTaskButton primary onClick={newFieldTask}>
                {t('new_task', 'New Task')}
              </StyledNewTaskButton>
            </AuthRender>
          )}
        </Header>
        <NeedsAttention />
        <ContentContainer>
          <PlanningTabs
            scheduleData={scheduleData}
            backlogData={backlogData}
            isFetching={isFetching || isAssetsLoading}
            workerData={crewData.workers}
            crewData={crewData.crewsSg}
            isFetchingWorkers={isFetchingCrewsWorkers || isAssetsLoading}
          />
          <Calendar />
        </ContentContainer>
      </PageContainer>
      {showNewTaskModal && (
        <NewTaskDialog
          onClose={closeModal}
          onConfirm={saveTask}
          taskSource={TaskSourceField.MANUAL}
        />
      )}
      {showConfirmationModal && (
        <Dialog
          isOpen={true}
          onClose={onCancelModal}
          header={'Confirm'}
          footer={getConfirmationFooter()}
        >
          <div>{'Are you sure you want to close without selecting service group'}</div>
        </Dialog>
      )}
    </>
  );
};

const ManageOverviewProvider = () => (
  <PlanningProvider>
    <ManageOverview />
  </PlanningProvider>
);

export default ManageOverviewProvider;
